import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import GameConfig from '../../config/gameConfig';

// Football Card Flip Memory Game
// Self-contained component with simple flip animations and scoreboard.
export default function FootballMemoryGame({ open, onClose }) {
    // Use game config for all settings
    const gameSettings = GameConfig.memoryGame;
    const ICONS = useMemo(() => gameSettings.cardIcons, [gameSettings.cardIcons]);

    const makeDeck = useCallback(() => {
        const pairs = ICONS.map((src, i) => ({ id: `p-${i}-a`, value: src }))
            .concat(ICONS.map((src, i) => ({ id: `p-${i}-b`, value: src })));
        // Fisher-Yates shuffle
        for (let i = pairs.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [pairs[i], pairs[j]] = [pairs[j], pairs[i]];
        }
        return pairs;
    }, [ICONS]);

    const [deck, setDeck] = useState(() => makeDeck());
    const [flipped, setFlipped] = useState([]);
    const [matched, setMatched] = useState(new Set());
    const [moves, setMoves] = useState(0);
    const [won, setWon] = useState(false);
    const [lost, setLost] = useState(false);
    const lockRef = useRef(false);
    const previewTimerRef = useRef(null);
    const [previewing, setPreviewing] = useState(false);
    const [timeLeft, setTimeLeft] = useState(gameSettings.timeLimit);
    const [timerStarted, setTimerStarted] = useState(false);
    const timerRef = useRef(null);
    const neededPairs = gameSettings.pairsNeeded;

    const startNewGame = useCallback(() => {
        if (previewTimerRef.current) {
            clearTimeout(previewTimerRef.current);
            previewTimerRef.current = null;
        }
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        const newDeck = makeDeck();
        setDeck(newDeck);
        setMatched(new Set());
        setMoves(0);
        setWon(false);
        setLost(false);
        setFlipped([]); // No initial reveal
        setPreviewing(false);
        lockRef.current = false;
        setTimeLeft(gameSettings.timeLimit);
        setTimerStarted(false); // Timer will start on first flip
    }, [makeDeck, gameSettings.timeLimit]);

    const startTimer = useCallback(() => {
        if (!timerStarted && !timerRef.current) {
            setTimerStarted(true);
            timerRef.current = setInterval(() => {
                setTimeLeft((t) => t - 1);
            }, 1000);
        }
    }, [timerStarted]);

    useEffect(() => {
        if (!open) return;
        startNewGame();
        return () => {
            if (previewTimerRef.current) {
                clearTimeout(previewTimerRef.current);
                previewTimerRef.current = null;
            }
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            lockRef.current = false;
        };
    }, [open, startNewGame]);

    useEffect(() => {
        // Win as soon as neededPairs are found (no need to match all cards)
        if (Math.floor(matched.size / 2) >= neededPairs && !won) {
            setWon(true);
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
        }
    }, [matched, neededPairs, won]);

    // Timer end -> evaluate lose if not enough pairs
    useEffect(() => {
        if (timeLeft <= 0 && !won && !lost) {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            if (Math.floor(matched.size / 2) >= neededPairs) {
                setWon(true);
            } else {
                setLost(true);
            }
        }
    }, [timeLeft, won, lost, matched, neededPairs]);

    const onCardClick = (card) => {
        if (lockRef.current || previewing || won || lost) return;
        if (matched.has(card.id) || flipped.includes(card.id)) return;
        
        // Start timer on first flip if not already started
        if (gameSettings.startTimerOnFirstFlip && flipped.length === 0) {
            startTimer();
        }
        
        const nextFlipped = [...flipped, card.id];
        setFlipped(nextFlipped);
        if (nextFlipped.length === 2) {
            lockRef.current = true;
            setMoves((m) => m + 1);
            const [aId, bId] = nextFlipped;
            const a = deck.find((c) => c.id === aId);
            const b = deck.find((c) => c.id === bId);
            const isMatch = a && b && a.value === b.value;
            setTimeout(() => {
                if (isMatch) {
                    setMatched((prev) => new Set([...prev, aId, bId]));
                    setFlipped([]);
                } else {
                    setFlipped([]);
                }
                lockRef.current = false;
            }, gameSettings.flipAnimationDuration);
        }
    };

    if (!open) return null;

    // 4x4 grid on small, 4x4/5x? We have 16 cards (8 pairs)
    return (
        <div className="absolute inset-0 z-50 flex flex-col">
            {/* Transparent background with subtle overlay */}
            <div className="absolute inset-0 bg-black/30" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(59,130,246,0.05),transparent_50%)]" />
            
            {/* Header / Scoreboard */}
            <div className="relative flex items-center justify-between px-4 sm:px-6 py-4 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 border-b border-slate-700 shadow-lg">
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-white/90 to-white/70 flex items-center justify-center shadow-lg border border-white/20">
                            <img 
                                src="/favicon.ico" 
                                alt="SceneShop Memory Game" 
                                className="w-8 h-8 object-contain"
                            />
                        </div>
                        <div>
                            <h1 className="font-bold text-white text-lg sm:text-xl">
                                Memory Challenge
                            </h1>
                            <p className="text-sm text-slate-300">Match pairs to win prizes!</p>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 text-sm">
                    <div className="hidden sm:flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/80 border border-slate-600">
                        <span className="text-slate-300 text-xs font-medium">Moves</span>
                        <span className="font-bold text-blue-400">{moves}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/80 border border-slate-600">
                        <span className="text-slate-300 text-xs font-medium">Pairs</span>
                        <span className="font-bold text-emerald-400">{Math.floor(matched.size / 2)}/{neededPairs}</span>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-700/80 border border-slate-600">
                        <span className="text-slate-300 text-xs font-medium">⏱</span>
                        <span className={`font-bold ${timeLeft <= 5 ? 'text-red-400 animate-pulse' : timerStarted ? 'text-orange-400' : 'text-slate-400'}`}>
                            {timerStarted ? Math.max(0, timeLeft) : gameSettings.timeLimit}s
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={() => {
                            startNewGame();
                        }}
                        className="px-4 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-lg border border-blue-500"
                        title="Restart"
                    >
                        🔄 Restart
                    </button>
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold transition-all duration-200 hover:scale-105 shadow-lg border border-red-500"
                    >
                        ✕ Close
                    </button>
                </div>
            </div>
            {/* Win condition banner */}
            <div className="relative px-4 sm:px-6 py-3 border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 via-blue-600/20 to-purple-600/20" />
                <div className="relative flex items-center justify-center gap-3">
                    <span className="text-2xl">🏆</span>
                    <div className="flex items-center gap-3">
                        <span className="text-sm sm:text-base font-semibold text-white">
                            Find {neededPairs} pairs in {gameSettings.timeLimit} seconds to win an 
                            <span className="mx-1 px-2 py-1 rounded-md bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold">
                                {gameSettings.prizeName}!
                            </span>
                        </span>
                        <img 
                            src={gameSettings.prizeImage} 
                            alt="Prize preview" 
                            className="w-8 h-8 sm:w-10 sm:h-10 object-contain rounded-md shadow-lg border border-white/20"
                        />
                    </div>
                </div>
            </div>
            {/* Grid */}
            <div className="relative flex-1 flex items-center justify-center p-4 sm:p-8">
                <div className="grid grid-cols-4 gap-3 sm:gap-4 w-full max-w-2xl">
                    {deck.map((card) => {
                        const isFaceUp = flipped.includes(card.id) || matched.has(card.id);
                        const isMatched = matched.has(card.id);
                        return (
                            <button
                                key={card.id}
                                type="button"
                                aria-label="memory-card"
                                onClick={() => onCardClick(card)}
                                className={`relative h-20 sm:h-24 md:h-28 rounded-xl transition-all duration-300 hover:scale-105 ${
                                    !isFaceUp ? 'hover:shadow-lg hover:shadow-blue-500/25' : ''
                                } ${isMatched ? 'ring-2 ring-emerald-400/50' : ''}`}
                                style={{
                                    perspective: '1000px',
                                    transformStyle: 'preserve-3d',
                                    transform: isFaceUp ? 'rotateY(180deg)' : 'rotateY(0deg)'
                                }}
                            >
                                {/* back: SceneShop logo with gradient */}
                                <div
                                    className="absolute inset-0 rounded-xl border border-white/20 flex items-center justify-center overflow-hidden group"
                                    style={{ backfaceVisibility: 'hidden' }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900" />
                                    <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-purple-600/10 to-indigo-600/20" />
                                    <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-gradient-to-br from-blue-500/10 to-purple-500/10" />
                                    <div className="relative flex flex-col items-center justify-center gap-1">
                                        <img src="/favicon.ico" alt="SceneShop logo" className="w-8 h-8 sm:w-10 sm:h-10 opacity-90 drop-shadow-lg" />
                                        <div className="text-[10px] text-white/60 font-medium">SceneShop</div>
                                    </div>
                                    {/* Subtle shine effect */}
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/5 via-transparent to-transparent" />
                                </div>
                                {/* front: revealed card with attractive styling */}
                                <div
                                    className="absolute inset-0 rounded-xl border-2 flex items-center justify-center overflow-hidden"
                                    style={{ 
                                        transform: 'rotateY(180deg)', 
                                        backfaceVisibility: 'hidden',
                                        borderColor: isMatched ? '#10b981' : '#3b82f6'
                                    }}
                                >
                                    <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-gray-100" />
                                    {isMatched && (
                                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/50 via-emerald-50/30 to-emerald-100/50" />
                                    )}
                                    <div className="relative flex items-center justify-center w-full h-full">
                                        <img 
                                            src={card.value} 
                                            alt="card icon" 
                                            className="w-2/3 h-2/3 object-contain drop-shadow-md filter"
                                            style={{
                                                filter: isMatched ? 'brightness(1.1) saturate(1.2)' : 'brightness(1) saturate(1)'
                                            }}
                                        />
                                    </div>
                                    {/* Success overlay for matched cards */}
                                    {isMatched && (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <div className="absolute top-1 right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center">
                                                <span className="text-white text-xs">✓</span>
                                            </div>
                                        </div>
                                    )}
                                    {/* Card shine effect */}
                                    <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-white/20 via-transparent to-transparent pointer-events-none" />
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
            {/* Win overlay */}
            {(won || lost) && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 backdrop-blur-sm">
                    <div className="relative max-w-md mx-4">
                        {/* Background with gradient and blur effect */}
                        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/90 via-slate-900/90 to-indigo-900/90 rounded-3xl backdrop-blur-md" />
                        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-transparent rounded-3xl" />
                        
                        <div className="relative px-8 py-8 text-center border border-white/20 rounded-3xl shadow-2xl">
                            {won ? (
                                <>
                                    <div className="mb-4">
                                        <div className="text-4xl mb-2">🏆</div>
                                        <div className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 bg-clip-text text-transparent mb-2">
                                            Victory!
                                        </div>
                                        <div className="text-sm text-emerald-300 font-semibold mb-3">
                                            🎉 Congratulations! 🎉
                                        </div>
                                    </div>
                                    <div className="mb-4">
                                        <img 
                                            src={gameSettings.prizeImage} 
                                            alt={`${gameSettings.prizeName} Prize`} 
                                            className="w-32 h-32 sm:w-40 sm:h-40 mx-auto object-contain rounded-lg shadow-lg"
                                        />
                                    </div>
                                    <div className="text-white/90 mb-6 leading-relaxed text-sm sm:text-base">
                                        You found <span className="font-bold text-emerald-300">{neededPairs} pairs</span> in time!<br/>
                                        <span className="text-blue-300 font-semibold">Claim your {gameSettings.prizeName}!</span>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="mb-4">
                                        <div className="text-6xl mb-2">⏰</div>
                                        <div className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text text-transparent mb-2">
                                            Time's Up!
                                        </div>
                                    </div>
                                    <div className="text-white/90 mb-6 leading-relaxed">
                                        Find <span className="font-bold text-blue-300">{neededPairs} pairs</span> within {gameSettings.timeLimit} seconds<br/>
                                        to win the <span className="text-purple-300 font-semibold">{gameSettings.prizeName}</span>.
                                    </div>
                                </>
                            )}
                            <div className="flex items-center justify-center gap-3">
                                <button
                                    type="button"
                                    onClick={() => {
                                        startNewGame();
                                    }}
                                    className="px-6 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                                >
                                    🎮 Play Again
                                </button>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-6 py-3 rounded-xl border border-white/30 text-white hover:bg-white/10 font-medium transition-all duration-200 hover:scale-105 backdrop-blur-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Lightweight confetti effect */}
            {won && (
                <ConfettiOverlay />
            )}
        </div>
    );
}

function ConfettiOverlay() {
    const [particles, setParticles] = useState([]);
    useEffect(() => {
        // Football-themed colors with gold accents
        const colors = [
            '#FFD700', '#FFA500', '#32CD32', '#1E90FF', '#FF6347', 
            '#9370DB', '#20B2AA', '#FF69B4', '#00CED1', '#98FB98'
        ];
        const shapes = ['circle', 'square', 'triangle'];
        
        const arr = Array.from({ length: 120 }).map((_, i) => ({
            id: i,
            left: Math.random() * 100,
            delay: Math.random() * 1.5,
            duration: 3 + Math.random() * 2,
            size: 8 + Math.random() * 12,
            color: colors[i % colors.length],
            shape: shapes[i % shapes.length],
            rotation: Math.random() * 360,
            driftX: -20 + Math.random() * 40
        }));
        setParticles(arr);
        const t = setTimeout(() => setParticles([]), GameConfig.memoryGame.confettiDuration);
        return () => clearTimeout(t);
    }, []);
    
    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
            {particles.map(p => {
                const shapeStyle = {
                    borderRadius: p.shape === 'circle' ? '50%' : p.shape === 'triangle' ? '0' : '2px',
                    ...(p.shape === 'triangle' && {
                        width: 0,
                        height: 0,
                        borderLeft: `${p.size/2}px solid transparent`,
                        borderRight: `${p.size/2}px solid transparent`,
                        borderBottom: `${p.size}px solid ${p.color}`,
                        background: 'transparent'
                    })
                };
                
                return (
                    <span
                        key={p.id}
                        className="absolute top-0"
                        style={{
                            left: `${p.left}%`,
                            width: p.shape === 'triangle' ? 0 : p.size,
                            height: p.shape === 'triangle' ? 0 : p.size,
                            background: p.shape === 'triangle' ? 'transparent' : p.color,
                            opacity: 0.9,
                            transform: `translateY(-20px) rotate(${p.rotation}deg)`,
                            animation: `confettiFall ${p.duration}s ease-out ${p.delay}s forwards`,
                            boxShadow: '0 0 6px rgba(255,255,255,0.3)',
                            ...shapeStyle
                        }}
                    />
                );
            })}
            <style>{`
                @keyframes confettiFall {
                    to {
                        transform: translateY(110vh) translateX(var(--drift-x, 0)) rotate(720deg);
                        opacity: 0.3;
                    }
                }
            `}</style>
        </div>
    );
}
